import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Users, Gem, Copy } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { crashApi } from '../services/crashApi'
import { hundredsToX } from '../utils/crashHelpers'
import { hapticNotify } from '../../../utils/haptic'
import ProfileLink from '../../../components/ProfileLink'
import TeamTag from '../../../components/TeamTag'

// Длина краткого id раунда для отображения (полный id копируется по кнопке)
const SHORT_ROUND_ID_LENGTH = 10

/**
 * Детальная страница раунда краша по uid (§9a).
 * GET /api/crash/history/{uid}/ — раунд со всеми ставками всех игроков.
 *
 * 404 round not found — раунд не найден ИЛИ ещё идёт (защита: в его строке уже
 * лежит точка краха). Показываем «раунд не найден» и возвращаем на список.
 *
 * Лимита на количество ставок нет — на людном раунде сотни строк,
 * поэтому рендерим через простой slice + «показать ещё», без виртуального списка
 * (MVP; при необходимости заменим на react-window).
 */
const CrashRound = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [visibleCount, setVisibleCount] = useState(20)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setError('')
    setData(null)
    setVisibleCount(20)
    crashApi.getRound(id)
      .then((resp) => {
        if (cancelled) return
        setData(resp)
        setIsLoading(false)
      })
      .catch((e) => {
        if (cancelled) return
        console.error('Crash round load error:', e)
        setError(t('crash.round.notFound'))
        setIsLoading(false)
      })
    return () => { cancelled = true }
  }, [id, t])

  if (isLoading) {
    return (
      <div className="px-4 py-6 lg:px-6">
        <div className="mx-auto max-w-3xl space-y-3">
          <div className="h-10 rounded-xl bg-surface animate-pulse" />
          <div className="h-32 rounded-xl bg-surface animate-pulse" />
          <div className="h-16 rounded-xl bg-surface animate-pulse" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="px-4 py-6 lg:px-6">
        <div className="mx-auto max-w-3xl space-y-4">
          <BackButton onClick={() => navigate(-1)} t={t} />
          <div className="text-center py-12 text-text-secondary">
            {error || t('crash.round.notFound')}
          </div>
        </div>
      </div>
    )
  }

  const round = data.round || {}
  const bets = Array.isArray(data.bets) ? data.bets : []
  const crashPoint = Number(round.crash_point || 0)
  const isLow = crashPoint < 200
  const visibleBets = bets.slice(0, visibleCount)

  // Id раунда: приоритет из данных, фолбэк на :id из URL. Краткий — для показа,
  // полный — копируется в буфер для саппорта (как в DoubleRound)
  const fullRoundId = String(round.id || id || '')
  const shortRoundId = fullRoundId.length > SHORT_ROUND_ID_LENGTH
    ? `${fullRoundId.slice(0, SHORT_ROUND_ID_LENGTH)}…`
    : fullRoundId

  const handleCopyRoundId = async () => {
    if (!fullRoundId) return
    try {
      await navigator.clipboard.writeText(fullRoundId)
      hapticNotify('success')
      toast.success(t('crash.round.idCopied'))
    } catch (error) {
      hapticNotify('error')
      toast.error(t('crash.round.copyFailed'))
    }
  }

  return (
    <div className="px-4 py-6 pb-20 lg:px-6 lg:pb-8">
      <div className="mx-auto max-w-3xl space-y-4">
        <BackButton onClick={() => navigate(-1)} t={t} />

        {/* Точка краха + общая статистика */}
        <div className="rounded-2xl bg-surface border border-white/10 p-4 text-center">
          <div className={`text-5xl font-black tabular-nums ${isLow ? 'text-red-500' : 'text-accent-primary'}`}>
            {hundredsToX(crashPoint)}×
          </div>
          <div className="mt-3 flex items-center justify-center gap-4 text-sm text-text-secondary">
            <span className="inline-flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {Number(round.players || 0)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Gem className="w-3.5 h-3.5 text-accent-primary" />
              {t('crash.round.bank')}: {Number(round.sum_bet || 0).toLocaleString('ru-RU')}
            </span>
          </div>
          {/* Id раунда для саппорта: кратко + копирование полного */}
          <div className="mt-3 flex items-center justify-center gap-2">
            <span className="text-xs text-white/90 font-mono truncate">{shortRoundId}</span>
            <button
              type="button"
              onClick={handleCopyRoundId}
              className="inline-flex items-center justify-center rounded-md border border-white/15 bg-dark-primary/40 p-1.5 text-white/80 hover:text-white"
              aria-label={t('crash.round.copyId')}
              title={t('crash.round.copyId')}
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Ставки */}
        <div className="rounded-2xl bg-surface border border-white/10 p-3">
          <h3 className="text-sm font-semibold text-text-secondary uppercase mb-3">
            {t('crash.round.bets')} ({bets.length})
          </h3>
          {bets.length === 0 ? (
            <div className="text-center py-6 text-text-secondary text-sm">
              {t('crash.round.noBets')}
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                {visibleBets.map((bet) => (
                  <RoundBetRow key={bet.chat_id} bet={bet} t={t} />
                ))}
              </div>
              {visibleCount < bets.length && (
                <button
                  onClick={() => setVisibleCount((c) => c + 50)}
                  className="w-full mt-3 py-2.5 rounded-lg bg-dark-secondary text-text-secondary hover:text-white border border-white/10"
                >
                  {t('crash.round.showMore')} ({bets.length - visibleCount})
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const BackButton = ({ onClick, t }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 p-2 rounded-full bg-surface border border-white/10 text-text-secondary hover:text-white"
  >
    <ArrowLeft className="w-5 h-5" />
    <span className="text-sm">{t('crash.round.back')}</span>
  </button>
)

const RoundBetRow = ({ bet, t }) => {
  const cashout = bet.cashout != null ? Number(bet.cashout) : null
  const status = bet.status
  const hasItems = Array.isArray(bet.bet_items) && bet.bet_items.length > 0
  const you = bet.you === true

  const statusText = status === 'won'
    ? `+${Number(bet.profit || 0).toLocaleString('ru-RU')}`
    : status === 'lost'
      ? `−${Number(bet.bet || 0).toLocaleString('ru-RU')}`
      : status === 'refunded'
        ? t('crash.history.refunded')
        : t('crash.history.placed')

  const statusColor = status === 'won'
    ? 'text-accent-primary'
    : status === 'lost'
      ? 'text-red-400'
      : 'text-text-secondary'

  return (
    <div className={`flex items-center gap-2.5 p-2 rounded-lg ${you ? 'bg-accent-primary/10 border border-accent-primary/30' : 'bg-dark-secondary/40'}`}>
      {/* Аватар ставки кликабелен: slug === null → игрок не кликабелен */}
      <ProfileLink slug={bet.slug} className="shrink-0 w-8 h-8 rounded-full overflow-hidden bg-dark-secondary border border-white/10 block">
        {bet.ava && <img src={bet.ava} alt={bet.name || ''} className="w-full h-full object-cover" loading="lazy" />}
      </ProfileLink>
      <div className="flex-1 min-w-0">
        {/* Банда — отдельной строкой над ником (строка ставки узкая, «[Банда]
            Ник» одной строкой обрезается). Обёртка — flex: у инлайнового
            бейджа появляется строчный strut, из-за него строки съезжают вниз */}
        {bet.team?.name && (
          <div className="flex min-w-0 leading-tight">
            <TeamTag team={bet.team} className="mr-0 min-w-0 text-[11px] leading-tight" />
          </div>
        )}
        <p className="truncate text-sm font-semibold leading-tight">
          <ProfileLink slug={bet.slug}>{bet.name || '—'}</ProfileLink>
        </p>
        {hasItems && (
          <div className="flex items-center gap-1.5 mt-1.5">
            {bet.bet_items.slice(0, 4).map((item, i) => (
              <div
                key={i}
                className="shrink-0 w-10 h-10 rounded-md overflow-hidden border"
                style={{ borderColor: item.color || '#444' }}
                title={`${item.name} · ${item.cost}`}
              >
                {item.img && <img src={item.img} alt="" className="w-full h-full object-cover" loading="lazy" />}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="shrink-0 text-right">
        {cashout != null && (
          <div className="text-sm font-bold text-accent-primary tabular-nums">
            {hundredsToX(cashout)}×
          </div>
        )}
        <div className={`text-xs font-medium tabular-nums ${statusColor}`}>
          {statusText}
        </div>
      </div>
      {bet.win_skin && (
        <div
          className="shrink-0 w-8 h-8 rounded overflow-hidden border"
          style={{ borderColor: bet.win_skin.color || '#444' }}
          title={`${bet.win_skin.name} · ${bet.win_skin.cost}`}
        >
          {bet.win_skin.img && <img src={bet.win_skin.img} alt="" className="w-full h-full object-cover" loading="lazy" />}
        </div>
      )}
    </div>
  )
}

export default CrashRound
